import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, ShieldCheck, EyeOff, Radar, ArrowRight, BookLock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { TRAITS } from "@/lib/traits";
import SupervisorCard from "@/components/SupervisorCard";
import { useAuth } from "@/context/AuthContext";

export default function Landing() {
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [query, setQuery] = useState("");
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/supervisors", { params: { sort: "reviews", limit: 6 } }).then((r) => setFeatured(r.data.items || []));
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/supervisors?search=${encodeURIComponent(query)}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative lb-hero-mesh border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 lb-grain opacity-60" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="max-w-3xl lb-rise">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-mono uppercase tracking-widest text-emerald-700 mb-6">
              <EyeOff size={13} /> 100% Anonymous Reviews
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-slate-900" style={{ fontWeight: 900 }}>
              Know your boss<br />
              <span className="lb-underline">before you</span> commit.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Honest, anonymous feedback from non-supervisory employees on the leaders they
              actually work under. Rated across 7 leadership traits that matter.
            </p>

            <form onSubmit={doSearch} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="hero-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search a supervisor or company..."
                  className="pl-11 h-12 bg-white border-slate-300 text-base"
                />
              </div>
              <Button data-testid="hero-search-button" type="submit" className="h-12 px-7 bg-slate-900 hover:bg-emerald-600 rounded-lg text-base gap-2">
                Search <ArrowRight size={18} />
              </Button>
            </form>

            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><ShieldCheck size={15} className="text-emerald-600" /> Verified non-supervisory employees</span>
              <span className="flex items-center gap-1.5"><Radar size={15} className="text-emerald-600" /> 8-trait leadership radar</span>
              <Link to="/vault" data-testid="landing-vault-link" className="flex items-center gap-1.5 hover:text-emerald-700"><BookLock size={15} className="text-emerald-600" /> Private encrypted Documentation Vault</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trait spotlight */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-2">What we measure</p>
            <h2 className="font-display text-4xl text-slate-900" style={{ fontWeight: 800 }}>The 7 leadership traits</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TRAITS.map((t, i) => (
            <div key={t.key} className="lb-rise bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-500/40 hover:shadow-lg transition-all" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-3">
                <t.icon size={20} className="text-emerald-600" />
              </div>
              <h3 className="font-display text-xl text-slate-900" style={{ fontWeight: 700 }}>{t.label}</h3>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">{t.description}</p>
            </div>
          ))}
          <div className="bg-slate-900 rounded-xl p-5 flex flex-col justify-center lb-grain">
            <h3 className="font-display text-2xl text-white" style={{ fontWeight: 800 }}>Your voice is anonymous.</h3>
            <p className="text-slate-400 text-sm mt-2">We never reveal who wrote a review. Help others decide.</p>
            <Button
              onClick={() => (user ? navigate("/supervisors") : setAuthOpen(true))}
              className="mt-4 self-start bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full font-semibold"
            >
              Write a review
            </Button>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-2">Most reviewed</p>
            <h2 className="font-display text-4xl text-slate-900" style={{ fontWeight: 800 }}>Featured supervisors</h2>
          </div>
          <Button variant="ghost" onClick={() => navigate("/supervisors")} className="gap-1.5 text-slate-600 hover:text-emerald-700">
            Browse all <ArrowRight size={16} />
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((s) => (
            <SupervisorCard key={s.id} sup={s} />
          ))}
        </div>
      </section>
    </div>
  );
}
